/* eslint-disable react/prop-types */
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';

import { getCountsByEntry } from '../../../../utils/security-question-utils';
import InputWithLabelV2 from '../InputWithLabelV2/InputWithLabelV2';
import SelectWithLabelV2 from '../SelectWithLabelV2/SelectWithLabelV2';

const SecurityQuestionsAndAnswersV2 = ({
  questions,
  answers,
  setQuestions,
  setAnswers,
  questionLabels,
  answerLabels,
  changeHandlers,
}) => {
  const [questionOptions, setQuestionOptions] = useState([]);

  const getQuestionErrorDesc = (value, state, countsByEntry) => (
    (value?.length && countsByEntry[value] > 1) || (state === 2 && !value?.length)
      ? 'You must select 3 different security questions'
      : null
  );

  const getAnswerErrorDesc = (value, state) => (
    state === 2 && !value?.trim()?.length
      ? 'Must not be blank'
      : null
  );

  const handleQuestionSelect = (ix, value) => {
    const questionsCopy = structuredClone(questions);

    questionsCopy[ix].value = value; // ID of selected question
    questionsCopy[ix].state = 2; // 0/undefined = no change yet, 2 = fully changed and error-decorated (skip debounce)

    const countsByEntry = getCountsByEntry(questionsCopy.filter((q) => q.value?.length > 0).map((q) => q.value));

    questionsCopy.forEach((q) => {
      q.error = getQuestionErrorDesc(q.value, q.state, countsByEntry);
    });

    setQuestions(questionsCopy);
  };

  const handleAnswerChange = (ix, value) => {
    const answersCopy = [...answers];

    const a = answersCopy[ix];

    a.value = value.trimStart();

    const { state } = a; // 0/undefined = no change yet, 1 = pending debounce time, 2 = fully changed and error-decorated

    a.error = getAnswerErrorDesc(a.value, state);

    if (!state) {
      a.state = 1;
    }

    setAnswers(answersCopy);

    if (!state) {
      setTimeout(
        () => {
          const answersCopy2 = [...answers];
          const a2 = answersCopy2[ix];

          a2.state = 2;
          a2.error = getAnswerErrorDesc(a2.value, 2);

          setAnswers(answersCopy2);
        },
        500,
      );
    }
  };

  useEffect(
    () => {
      if (!questions?.length) { return; }

      const countsByEntry = getCountsByEntry(questions.filter((q) => q.value?.length > 0).map((q) => q.value));

      const validatedQuestions = questions.map(
        (q) => ({
          ...q,
          error: getQuestionErrorDesc(q.value, q.state, countsByEntry),
        }),
      );

      const changedEntry = questions.find(
        (q, ix) => (
          (validatedQuestions[ix].error?.length && validatedQuestions[ix].error !== q.error)
          || (validatedQuestions[ix].error?.length !== q.error?.length)
        ),
      );

      if (changedEntry) {
        setQuestions(validatedQuestions);
      }
    },
    [questions],
  );

  useEffect(
    () => {
      if (!questionOptions?.length) {
        axios.get('/iam/lookup/security-questions')
          .then(
            ({ data }) => {
              setQuestionOptions(
                [{ display: 'Select Security Question', value: '' }]
                  .concat(data.map((q) => ({ display: q.question, value: q.id }))),
              );

              if (changeHandlers?.questionOptionsPromise?.resolveCb) {
                changeHandlers.questionOptionsPromise.resolveCb();
              }
            },
          );
      }
    },
    [questionOptions],
  );

  useEffect(
    () => {
      if (!answers?.length) { return; }

      const validatedAnswers = answers.map(
        (a) => ({
          ...a,
          error: getAnswerErrorDesc(a.value, a.state),
        }),
      );

      const changedEntry = answers.find(
        (a, ix) => (
          (validatedAnswers[ix].error?.length && validatedAnswers[ix].error !== a.error)
          || (validatedAnswers[ix].error?.length !== a.error?.length)
        ),
      );

      if (changedEntry) {
        setAnswers(validatedAnswers);
      }
    },
    [answers],
  );

  useEffect(
    () => {
      changeHandlers.questions = handleQuestionSelect;
      changeHandlers.answers = handleAnswerChange;
    },
    [questions, setQuestions, answers, setAnswers],
  );

  return (
    <>
      <SelectWithLabelV2
        id="Security_Question_1__c"
        label={questionLabels[0]}
        value={questions[0].value}
        options={questionOptions}
        errorMsg={questions[0].error}
        onChange={({ target: { value } }) => handleQuestionSelect(0, value)}
      />

      <InputWithLabelV2
        id="Security_Question_1_Answer__c"
        label={answerLabels[0]}
        value={answers[0].value}
        maxLength="100"
        errorMsg={answers[0].error}
        onChange={({ target: { value } }) => handleAnswerChange(0, value)}
      />

      <SelectWithLabelV2
        id="Security_Question_2__c"
        label={questionLabels[1]}
        value={questions[1].value}
        options={questionOptions}
        errorMsg={questions[1].error}
        onChange={({ target: { value } }) => handleQuestionSelect(1, value)}
      />

      <InputWithLabelV2
        id="Security_Question_2_Answer__c"
        label={answerLabels[1]}
        value={answers[1].value}
        maxLength="100"
        errorMsg={answers[1].error}
        onChange={({ target: { value } }) => handleAnswerChange(1, value)}
      />

      <SelectWithLabelV2
        id="Security_Question_3__c"
        label={questionLabels[2]}
        value={questions[2].value}
        options={questionOptions}
        errorMsg={questions[2].error}
        onChange={({ target: { value } }) => handleQuestionSelect(2, value)}
      />

      <InputWithLabelV2
        id="Security_Question_3_Answer__c"
        label={answerLabels[2]}
        value={answers[2].value}
        maxLength="100"
        errorMsg={answers[2].error}
        onChange={({ target: { value } }) => handleAnswerChange(2, value)}
      />
    </>
  );
};

SecurityQuestionsAndAnswersV2.propTypes = {
  questions: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string,
      state: PropTypes.number,
    }),
  ).isRequired,
  answers: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string,
      state: PropTypes.number,
    }),
  ).isRequired,
  setQuestions: PropTypes.func.isRequired,
  setAnswers: PropTypes.func.isRequired,
  questionLabels: PropTypes.arrayOf(PropTypes.string),
  answerLabels: PropTypes.arrayOf(PropTypes.string),
  changeHandlers: PropTypes.shape({
    questions: PropTypes.func,
    answers: PropTypes.func,
    questionOptionsPromise: PropTypes.shape({
      promise: PropTypes.instanceOf(Promise),
      resolveCb: PropTypes.func,
      rejectCb: PropTypes.func,
    }).isRequired,
  }).isRequired,
};

SecurityQuestionsAndAnswersV2.defaultProps = {
  questionLabels: ['Security Question 1', 'Security Question 2', 'Security Question 3'],
  answerLabels: ['Security Question 1 Answer', 'Security Question 2 Answer', 'Security Question 3 Answer'],
};

export default SecurityQuestionsAndAnswersV2;
